import React, { useEffect, useRef, useState } from "react";
import DashboardLayout from "@layouts/dashboard/dashboard-layout";
import { getValue } from "@utils/lodash";
import NoAccessPage from "@components/common/NoAccess";
import { useNavigate } from "react-router-dom";
import ScrollableReactTable from "@components/ScrollableDatatable";
import DragAndDropFileComponent from "@common/FileUpload/file-drag-drop";
import { mediaUpload } from "@services/upload.service";
import DocumentImageList from "@common/FileUpload/document-image-list";
import InputField from "@common/InputRuleFeild";
import SimpleReactValidator from "simple-react-validator";
import "./index.scss";
import {
  ctcReimbursementApplicationForm,
  ctcReimbursementApplicationFormSubmit,
} from "@services/dashboard.service";
import Loader from "@components/common/Loader";
import ReimburseFormBuilder from "@components/common/ReimburseFormBuilder";
import {
  removeNullOrUndefinedProperties,
  sortJSONObjectArray,
} from "@common/text-helpers";
import {
  checkBoolean,
  FormRequestHelper,
} from "@components/common/ReimburseFormBuilder/request-helper";
import { toast } from "react-toastify";

// Define the interface for a single form field
interface FormField {
  field_name: string;
  field_type: string;
  field_value: string | object | boolean; // field_value can be a string, file object, or boolean (for checkbox)
  field_description: string;
  required: string;
  form_group: string;
  note?: string;
}

// Define the type for fieldMap and payload (a dynamic key-value pair object)
interface DynamicObject {
  [key: string]: string | object | boolean;
}

// Define the type for simpleValidator (assuming it's a ref)
interface SimpleValidator {
  current: {
    allValid: () => boolean;
    showMessages: () => void;
  };
}

// Define the props for handleSubmit
interface HandleSubmitProps {
  form: FormField[];
  simpleValidator: React.RefObject<SimpleValidator>;
  forceUpdate: (value: number) => void;
}

function ReimbursementDashboardEdit(props: any) {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const urlParams = Object.fromEntries(urlSearchParams.entries());
  const navigate = useNavigate();
  /* -------------------------------------------------------------------------- */
  /*                               UseState Section                             */
  /* -------------------------------------------------------------------------- */
  const [isLoading, setIsLoading] = React.useState(true);
  const simpleValidator = useRef(new SimpleReactValidator());
  const [request, setRequest] = useState<any>({
    no_of_bills: "",
    amount: "",
    application_description: "",
    file_password: "",
    contact_no: "",
    attachment_ids: [],
  });

  // Form States
  const [form, setForm] = React.useState<any[]>([]);
  const [formInfo, setFormInfo] = useState({});
  const [mainInfo, setMainInfo] = useState({});
  const [, forceUpdate] = useState(0);
  // Add state for eligibility summary data
  const [eligibilitySummary, setEligibilitySummary] = useState<any[]>([]);
  const [eligibilityColumns, setEligibilityColumns] = useState<any[]>([]);

  /* -------------------------------------------------------------------------- */
  /*                               UseEffect Section                            */
  /* -------------------------------------------------------------------------- */
  useEffect(() => {
    if (Object.keys(urlParams).length === 0 && props.permissionAPITriggered) {
      getFormData();
    }
  }, [props.permissionAPITriggered]);

  useEffect(() => {
    if (Object.keys(urlParams).length > 0 && props.permissionAPITriggered) {
      getFormData();
    }
  }, [window.location.href, props.permissionAPITriggered]);

  const getFormData = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("stohrmToken");

      if (!token) {
        throw new Error("No token found in localStorage");
      }

      let payload = {
        token: token,
        template: urlParams.template,
        rcode: urlParams.reimbursement_code,
      };
      let userResponse = await ctcReimbursementApplicationForm(payload);
      if (userResponse) {
        // Process form fields
        let builder =
          getValue(userResponse, `data.form_fields.length`, 0) > 0 &&
          sortJSONObjectArray(
            getValue(userResponse, `data.form_fields`, []),
            "seq_no"
          ).map((item: any) => ({
            ...item,
            field_value: checkBoolean(getValue(item, `field_value`, "")),
          }));

        setForm(
          builder.filter(
            (item: object) =>
              getValue(item, `field_name`, "") !== "expense_type"
          )
        );
        
        // Process eligibility summary data
        const summaryData = getValue(userResponse, 'data.eligibility_summery', []);
        if (summaryData.length > 0) {
          // Extract unique column names to create columns
          const uniqueColumns = Array.from(
            new Set(summaryData.map((item: any) => item.column_name))
          );
          
          // Create columns array for the table
          const columns = uniqueColumns.map((columnName: unknown) => {
            // Find the item with this column name to get the title if available
            const columnItem = summaryData.find((item: any) => item.column_name === columnName);
            const title = getValue(columnItem, 'title', '');
            return {
              Header: columnName as string,
              accessor: (columnName as string).toLowerCase().replace(/\s+/g, '_'), // Convert "Column Name" to "column_name"
              title: title, // Store title for tooltip if needed
              style: { "white-space": "unset", overflow: "hidden" },
              width: 150,
            };
          });
          
          setEligibilityColumns(columns);
          
          // Transform the data for the table
          // We need to convert from column-based to row-based format
          const rowData: any = {};
          
          // Initialize with empty values
          uniqueColumns.forEach((columnName: unknown) => {
            rowData[(columnName as string).toLowerCase().replace(/\s+/g, '_')] = '';
          });
          
          // Fill in the values
          summaryData.forEach((item: any) => {
            const key = item.column_name.toLowerCase().replace(/\s+/g, '_');
            rowData[key] = item.column_value;
          });
          
          setEligibilitySummary([rowData]);
        }
      }
    } catch (error) {
      console.error("Error fetching summary data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const reimbursementColumns = React.useMemo(
    () => [
      {
        Header: "Reimbursement Type",
        accessor: "reimbursement_type",
        style: { "white-space": "unset", overflow: "hidden" },
        width: 250,
      },
      {
        Header: "Eligibility",
        accessor: "eligibility",
        style: { "white-space": "unset" },
        width: 150,
      },
      {
        Header: "Applied",
        accessor: "applied",
        style: { "white-space": "unset" },
        width: 120,
      },
      {
        Header: "Approved",
        accessor: "approved",
        style: { "white-space": "unset" },
        width: 120,
      },
      {
        Header: "Paid",
        accessor: "paid",
        style: { "white-space": "unset" },
        width: 120,
      },
      {
        Header: "Balance",
        accessor: "balance",
        style: { "white-space": "unset" },
        width: 150,
      },
    ],
    []
  );

  const reimbursementData = React.useMemo(
    () => [
      {
        reimbursement_type: "Telephone and Internet",
        eligibility: "20,000",
        applied: 0,
        approved: 0,
        paid: 0,
        balance: "20,000",
      },
    ],
    []
  );

  const handleSubmit = async () => {
    try {
      const formValid = simpleValidator.current.allValid();
      if (!formValid) {
        simpleValidator.current.showMessages();
        forceUpdate(1);
        return; // Exit early if validation fails
      }

      // Create FormData for submission
      const formData = new FormData();
      
      // Log the form data before submission for debugging
      console.log("Form data before submission:", form);
      
      // Add all fields to formData
      form.forEach((field: any) => {
        if (field.field_value !== undefined && field.field_value !== null) {
          // Special handling for file fields
          if (field.field_type === "file" || field.data_type === "file") {
            // Check if field_value is a File object
            if (field.field_value instanceof File) {
              formData.append(field.field_name, field.field_value);
            } else if (field.file_object && field.file_object instanceof File) {
              // If the file is stored in a file_object property
              formData.append(field.field_name, field.file_object);
            }
          } else {
            // Special handling for checkbox fields (like self_declare)
            if (field.field_type === "checkbox") {
              const booleanValue = field.field_value === true || field.field_value === "true";
              formData.append(field.field_name, booleanValue ? "Y" : "N");
            } else {
              // Convert non-file values to string
              formData.append(field.field_name, String(field.field_value));
            }
          }
        }
      });
      
      // Add document attachments if any
      if (documents && documents.length > 0) {
        documents.forEach((doc: any, index: number) => {
          if (doc && doc.id) {
            formData.append(`attachment_ids[${index}]`, doc.id);
          }
        });
      }
      
      // Add required parameters
      const token = localStorage.getItem("stohrmToken");
      formData.append('token', token || '');
      formData.append('template', urlParams.template || '');
      formData.append('r_code', urlParams.reimbursement_code || '');
      // Log the FormData entries for debugging
      Array.from(formData.entries()).forEach(pair => {
        console.log(pair[0] + ': ' + pair[1]);
      });

      try {
        const resp = await ctcReimbursementApplicationFormSubmit(formData);
        if (resp) {
          toast.success(getValue(resp, "message", ""));
          navigate("/reimbursement/dashboard");
        }
      } catch (error) {
        console.error("Error submitting form:", error);
      }
    } catch (error) {
      console.error("Error in form validation:", error);
      toast.error("Form validation failed");
    }
  };

  const getTrProps = (rowInfo: any) => {
    if (getValue(rowInfo, `id`, "")) {
      return {
        style: {
          //   background:  'red',
          //   color: 'black'
        },
      };
    }
    return {};
  };

  const [selectedIDs, setSelectedIDs] = useState<any>([]);
  const getTableState = (value: object) => {
    let selectedIndexList = Object.keys(
      getValue(value, `selectedRowIds`, {})
    ).map((item) => parseInt(item));
    let selectedIds =
      reimbursementData.length > 0
        ? reimbursementData.filter((_item: object, index: number) =>
            selectedIndexList.includes(index)
          )
        : [];
    if (selectedIds.length > 0) {
      setSelectedIDs(
        selectedIds.map((item: object) => getValue(item, `id`, ""))
      );
    } else {
      setSelectedIDs([]);
    }
  };

  const rowClick = (obj: object) => {
    // navigate(`/edit-user/${getValue(obj, `original.id`, "")}`);
  };

  /* -------------------------------------------------------------------------- */
  /*                               Upload Section                               */
  /* -------------------------------------------------------------------------- */
  const [documents, setDocuments] = useState<any>([]);
  const fileTypes = ["PDF", "JPEG", "XLSX", "CSV", "XLSM", "XLX", "JPG", "PNG"];
  const handleUploadDocuments = async (e: any) => {
    Array.from(e).forEach(async (item: any) => {
      let formData = new FormData();
      formData.append("file", item);
      try {
        let resp = await mediaUpload(formData);
        if (resp) {
          setDocuments((prevDocuments: any) => [
            ...prevDocuments,
            getValue(resp, `data`, {}),
          ]);
          e = null;
        } else {
          e = null;
        }
      } catch (error) {
        e = null;
      }
    });
  };
  const handleRemoveDocuments = (index: number) => {
    let list = documents.filter((item: object, i: number) => index !== i);
    setDocuments(list);
  };

  return (
    <>
      <DashboardLayout
        permissions={getValue(props, `tabPermissionList`, [])}
        subTabPermissionList={getValue(props, `subTabPermissionList`, [])}
      >
        {!isLoading &&
        !getValue(props, `subTabPermissionList`, []).includes("dashboard") ? (
          <NoAccessPage />
        ) : (
          <>
            <div className="dashboard-wrapper__header d-flex align-items-center justify-content-between">
              <h4 className="dashbaord-wrapper__header-title">
                Reimbursement Application Form
              </h4>
            </div>
            <div className="reimbursement-dashbaord-wrapper_section_container reimbursement-view-wrapper_section_container">
              {isLoading || !props.permissionAPITriggered ? (
                <Loader />
              ) : (
                <>
                  <div className="card bg-light mb-3">
                    <div className="card-header">Eligibility Summary</div>

                    <div
                      className="card-body bg-white"
                      style={{
                        borderBottomLeftRadius: "4px",
                        borderBottomRightRadius: "4px",
                      }}
                    >
                      <ScrollableReactTable
                        columns={eligibilityColumns}
                        data={eligibilitySummary}
                        getTableState={getTableState}
                        rowClick={rowClick}
                        checkboxlogo
                        selectedIDs={selectedIDs}
                        getTrProps={getTrProps}
                      />
                    </div>
                  </div>

                  <div className="card bg-light mb-3">
                    <div className="card-header">Form Details</div>

                    <div
                      className="card-body bg-white p-0"
                      style={{
                        borderBottomLeftRadius: "4px",
                        borderBottomRightRadius: "4px",
                      }}
                    >
                      <div className="reimubursement-application-wrapper">
                        <div className="raw-right-container">
                          <div className="form-details-container">
                            <div className="form-fields-container">
                              <ReimburseFormBuilder
                                fields={form}
                                setFields={setForm}
                                formOptions={formInfo}
                                simpleValidator={simpleValidator}
                                mainInfo={mainInfo}
                                formType="legacy"
                                layoutType="two-column"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="raw-footer">
                        <div>
                          <button className="ascent-button ascent-button--header-buttons ascent-button--primary ">
                            Cancel
                          </button>

                          <button
                            className="ascent-button ascent-button--header-buttons ascent-button--secondary ms-3"
                            onClick={handleSubmit}
                          >
                            Create
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </DashboardLayout>
    </>
  );
}

export default ReimbursementDashboardEdit;
